import { useCallback, useMemo, useState } from "react"

import { useMatch, useParams } from "react-router-dom"

import { DocumentEditModal, API_MODULE_DOCUMENT } from "@l2r-front/l2r-app-doc"
import { CatalogItemEditModal } from "@l2r-front/l2r-common"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { DeleteIcon, EditLocationIcon, ModeEditIcon, NoteAddRoundedIcon } from "@l2r-front/l2r-icons"
import { useNetworksStateContext, useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { MoreMenu, useIsMobileDevice } from "@l2r-front/l2r-ui"
import { NavigationOrigins, useNavigateWithSearchParams, useUrlParams } from "@l2r-front/l2r-utils"

import { APP_CITY } from "../../../../common/constants/appCity"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { MODULE_VERTICAL_SIGNING } from "../../../../common/constants/modules"
import { DETAIL_TAB_PARAM_KEY } from "../../../../common/constants/tabKeys"
import { VerticalSignDetailsFormPost } from "../../forms/VerticalSignDetailsForm"
import { VerticalSignPoleAddDocumentFormPost } from "../../forms/VerticalSignPoleAddDocumentForm"
import { VerticalSignPoleEditFormPost } from "../../forms/VerticalSignPoleEditForm/VerticalSignPoleEditForm.post"
import { useVerticalSignDelete } from "../../hooks/mutations/useVerticalSignDelete"
import { usePoleWithVerticalSigns } from "../../hooks/queries/verticalSigning/usePoleWithVerticalSigns"
import { useVerticalSignType } from "../../hooks/queries/verticalSigning/useVerticalSignTypes"

import { VerticalSignPoleDetailsSidebarContent } from "../VerticalSignPoleDetailsSidebarContent"

import * as Styled from "./VerticalSignPoleDetailsSidebar.styled"

export function VerticalSignPoleDetailsSidebar() {

    const [editModalOpen, setEditModalOpen] = useState(false)
    const [locationModalOpen, setLocationModalOpen] = useState(false)
    const [documentAddModalOpen, setDocumentAddModalOpen] = useState(false)

    const navigate = useNavigateWithSearchParams()
    const { setAlert } = useAlertsDispatchContext()
    const { signId: poleId, road } = useParams()
    const { data, isLoading } = usePoleWithVerticalSigns(poleId)
    const { t } = useTranslation(I18N_NAMESPACE)
    const isMobile = useIsMobileDevice()
    const readOnly = useRoleIsReadOnly()
    const { selectedNetwork } = useNetworksStateContext()
    const matchRoadCode = useMatch(`:slugId/${APP_CITY}/${MODULE_VERTICAL_SIGNING}/:road/code/:code/*`)

    const { getParam, setParam } = useUrlParams()
    const tabParam = getParam(DETAIL_TAB_PARAM_KEY)

    const tabValue = useMemo(() => {
        return tabParam ? parseInt(tabParam.replace("tab-", "")) : 0
    }, [tabParam])

    const title = useMemo(() => {
        if (data) {
            const is_multi = data.is_multi ||
                (data.vertical_signs.length > 1 && (data.vertical_signs.every(sign => !sign.is_main) || data.vertical_signs.filter(sign => sign.is_main).length > 1))
            return is_multi ? t(I18N_NAMESPACE, "containers.sidebarVerticalSignDetail.multiSigns.title")
                : data.vertical_signs.find(sign => sign.is_main)?.name || data.vertical_signs[0].name
        }
        return ""
    }, [data, t])

    const latLong = data?.geometry.coordinates

    const navigateBack = useCallback(() => {
        navigate(matchRoadCode ? `../${road}/code/${matchRoadCode.params.code}` : `../${road}`, {
            navigationOrigin: NavigationOrigins.ROAD_FROM_BACK_DETAIL,
            paramsToDelete: [DETAIL_TAB_PARAM_KEY],
        })
    }, [matchRoadCode, navigate, road])

    const selectedVerticalSign = useMemo(() => {
        if (!data) {
            return null
        }

        return data.vertical_signs[tabValue] || null
    }, [data, tabValue])

    const { data: selectedVerticalSignType } = useVerticalSignType(selectedVerticalSign?.vertical_sign_type, {}, { enabled: Boolean(selectedVerticalSign) })

    const deleteVerticalSignMutation = useVerticalSignDelete(selectedVerticalSign?.uuid, poleId, {
        onSuccess: () => setParam(DETAIL_TAB_PARAM_KEY, `tab-${Math.max(tabValue - 1, 0)}`),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.verticalSignDetailSidebar.edit.deleteSign.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.verticalSignDetailSidebar.edit.deleteSign.success"),
    })

    const deleteVerticalSignPoleMutation = useVerticalSignDelete(selectedVerticalSign?.uuid, poleId, {
        onSuccess: () => navigate(`../${road}`),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.verticalSignDetailSidebar.edit.deleteSignPole.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.verticalSignDetailSidebar.edit.deleteSignPole.success"),
    })

    const handleDeleteVerticalSign = useCallback(() => {
        setAlert({
            alertTitle: t(I18N_NAMESPACE, "containers.verticalSignDetailSidebar.edit.deleteSign.confirmation", { itemName: selectedVerticalSignType?.name }),
            alertText: data.vertical_signs.length > 1 ? null : t(I18N_NAMESPACE, "containers.verticalSignDetailSidebar.edit.deleteSignPole.confirmation"),
            cancelText: t(I18N_NAMESPACE, "containers.verticalSignDetailSidebar.edit.delete.cancel"),
            confirmText: t(I18N_NAMESPACE, "containers.verticalSignDetailSidebar.edit.delete.confirm"),
            callback: () => {
                data.vertical_signs.length > 1 ? deleteVerticalSignMutation.mutate() : deleteVerticalSignPoleMutation.mutate()
            },
            severity: "warning",
        })
    }, [data, selectedVerticalSignType, deleteVerticalSignMutation, deleteVerticalSignPoleMutation, setAlert, t])

    if (!data || isLoading) {
        return null
    }

    return <>
        <Styled.SidebarContent id="verticalSigning-sidebar"
            onBack={navigateBack}
            isMobile={isMobile}
            title={title}
            titleSideComponent={<MoreMenu
                menuConfig={[
                    {
                        enabled: !readOnly,
                        id: "edit-location",
                        label: t(I18N_NAMESPACE, "containers.verticalSignDetailSidebar.edit.location"),
                        icon: <EditLocationIcon color="objects/object-black" />,
                        props: {
                            onClick: () => setLocationModalOpen(true),
                        },
                    },
                    {
                        enabled: !readOnly,
                        id: "edit-properties",
                        label: t(I18N_NAMESPACE, "containers.verticalSignDetailSidebar.edit.properties", { itemName: selectedVerticalSignType?.name }),
                        icon: <ModeEditIcon color="objects/object-black" />,
                        props: {
                            onClick: () => setEditModalOpen(true),
                        },
                    },
                    {
                        enabled: selectedNetwork.modules.includes(API_MODULE_DOCUMENT) && !readOnly,
                        id: "add-document",
                        label: t(I18N_NAMESPACE, "containers.verticalSignDetailSidebar.addDocument"),
                        icon: <NoteAddRoundedIcon color="objects/object-black" />,
                        props: {
                            onClick: () => setDocumentAddModalOpen(true),
                        },
                    },
                    {
                        enabled: !readOnly,
                        id: "delete-sign",
                        label: t(I18N_NAMESPACE, "containers.verticalSignDetailSidebar.edit.delete.menu", { itemName: selectedVerticalSignType?.name }),
                        icon: <DeleteIcon color="objects/object-black" />,
                        props: {
                            onClick: () => handleDeleteVerticalSign(),
                        },
                    },
                ]} />}
        >
            <VerticalSignPoleDetailsSidebarContent />
        </Styled.SidebarContent>
        {data && <CatalogItemEditModal
            FormComponent={VerticalSignPoleEditFormPost}
            poleId={poleId}
            formTitle={t(I18N_NAMESPACE, "containers.verticalSignPoleLocationEditForm.title")}
            startingPosition={{ longitude: latLong[0], latitude: latLong[1] }}
            startingRoad={data?.linear_location.road}
            open={locationModalOpen}
            onClose={() => setLocationModalOpen(false)}
        />}
        {selectedVerticalSign && <CatalogItemEditModal
            formTitle={t(I18N_NAMESPACE, "containers.verticalSignDetailsForm.title")}
            typeTitle={t(I18N_NAMESPACE, "containers.verticalSignDetailsForm.type")}
            contentFieldLabel={t(I18N_NAMESPACE, "containers.verticalSignDetailsForm.content")}
            FormComponent={VerticalSignDetailsFormPost}
            signId={selectedVerticalSign.uuid}
            poleId={poleId}
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
        />}
        {documentAddModalOpen && <DocumentEditModal
            open={documentAddModalOpen}
            onClose={() => setDocumentAddModalOpen(false)}
            FormComponent={VerticalSignPoleAddDocumentFormPost}
            poleId={poleId}
        />}
    </>
}